import React, { useState, useEffect, useCallback } from "react";
import { useParams, Navigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
  CircularProgress,
  Avatar,
  Button,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CommentIcon from "@mui/icons-material/Comment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { updateMetaTags } from "../utils/seo";
import Footer from "../components/Footer";

interface HNStory {
  id: number;
  title: string;
  url?: string;
  score: number;
  by: string;
  time: number;
  descendants?: number;
  kids?: number[];
}

interface HNComment {
  id: number;
  by: string;
  text: string;
  time: number;
  kids?: number[];
  deleted?: boolean;
  dead?: boolean;
}

const HNStoryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [story, setStory] = useState<HNStory | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [comments, setComments] = useState<HNComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingStory, setLoadingStory] = useState(true);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  // Fetch story details
  useEffect(() => {
    const fetchStory = async () => {
      if (!id) return;

      try {
        setLoadingStory(true);
        const response = await fetch(
          `https://hacker-news.firebaseio.com/v0/item/${id}.json`,
        );
        const data = await response.json();
        setStory(data);

        // Update SEO
        updateMetaTags({
          title: `${data.title} - Hacker News`,
          description: `Score: ${data.score} | By: ${data.by} | Comments: ${
            data.descendants || 0
          }`,
          keywords: "hacker news, tech news, programming",
          canonical: `${window.location.origin}/news/${id}`,
          ogTitle: data.title,
          ogDescription: `Score: ${data.score} | By: ${data.by}`,
          ogUrl: `${window.location.origin}/news/${id}`,
          ogType: "article",
          twitterCard: "summary",
          twitterTitle: data.title,
          twitterDescription: `Score: ${data.score} | By: ${data.by}`,
        });

        // Reset iframe state
        setIframeLoading(true);
        setIframeError(false);
      } catch (error) {
        console.error("Error fetching story:", error);
      } finally {
        setLoadingStory(false);
      }
    };

    fetchStory();
  }, [id]);

  // Timeout to detect iframe load failures
  useEffect(() => {
    if (activeTab === 0 && iframeLoading && !iframeError && story?.url) {
      const timeout = setTimeout(() => {
        if (iframeLoading && iframeRef.current) {
          try {
            const iframeDoc = iframeRef.current.contentDocument;
            if (!iframeDoc || iframeDoc.location.href === "about:blank") {
              setIframeLoading(false);
              setIframeError(true);
            }
          } catch (e) {
            setIframeLoading(false);
            setIframeError(true);
          }
        }
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [activeTab, iframeLoading, iframeError, story?.url]);

  const fetchComment = async (commentId: number): Promise<HNComment | null> => {
    try {
      const response = await fetch(
        `https://hacker-news.firebaseio.com/v0/item/${commentId}.json`,
      );
      const comment = await response.json();
      return comment?.deleted || comment?.dead ? null : comment;
    } catch (error) {
      console.error("Error fetching comment:", error);
      return null;
    }
  };

  const fetchComments = useCallback(async (commentIds: number[]) => {
    setLoadingComments(true);
    try {
      const commentPromises = commentIds.slice(0, 20).map(fetchComment);
      const fetchedComments = await Promise.all(commentPromises);
      const validComments = fetchedComments.filter(
        (c): c is HNComment => c !== null,
      );
      setComments(validComments);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoadingComments(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 1 && story?.kids && comments.length === 0) {
      fetchComments(story.kids);
    }
  }, [activeTab, story?.kids, comments.length, fetchComments]);

  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor(Date.now() / 1000 - timestamp);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval !== 1 ? "s" : ""} ago`;
      }
    }
    return "just now";
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setIframeLoading(true);
    }
  };

  const CommentItem: React.FC<{ comment: HNComment; depth?: number }> = ({
    comment,
    depth = 0,
  }) => {
    const [expanded, setExpanded] = useState(true);
    const [replies, setReplies] = useState<HNComment[]>([]);
    const [loadingReplies, setLoadingReplies] = useState(false);

    const loadReplies = useCallback(async () => {
      if (!comment.kids || replies.length > 0) return;
      setLoadingReplies(true);
      try {
        const replyPromises = comment.kids.slice(0, 5).map(fetchComment);
        const fetchedReplies = await Promise.all(replyPromises);
        const validReplies = fetchedReplies.filter(
          (c): c is HNComment => c !== null,
        );
        setReplies(validReplies);
      } catch (error) {
        console.error("Error loading replies:", error);
      } finally {
        setLoadingReplies(false);
      }
    }, [comment.kids, replies.length]);

    useEffect(() => {
      if (expanded && comment.kids && depth < 3 && replies.length === 0) {
        loadReplies();
      }
    }, [expanded, comment.kids, depth, replies.length, loadReplies]);

    return (
      <Box
        sx={{
          ml: depth * 3,
          borderLeft: depth > 0 ? "2px solid" : "none",
          borderColor: "divider",
          pl: depth > 0 ? 2 : 0,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Avatar sx={{ width: 24, height: 24, bgcolor: "#ff6600" }}>
            <PersonIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {comment.by}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {getTimeAgo(comment.time)}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mb: 1,
            color: "text.secondary",
            "& a": { color: "#ff6600" },
          }}
          dangerouslySetInnerHTML={{ __html: comment.text || "" }}
        />

        {comment.kids && comment.kids.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: "none", color: "#ff6600" }}
            >
              {expanded ? "Collapse" : `Show ${comment.kids.length} replies`}
            </Button>
          </Box>
        )}

        {expanded && (
          <Box>
            {loadingReplies && (
              <CircularProgress size={20} sx={{ ml: 2, color: "#ff6600" }} />
            )}
            {replies.map((reply) => (
              <CommentItem key={reply.id} comment={reply} depth={depth + 1} />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  if (!id) {
    return <Navigate to="/news" replace />;
  }

  if (loadingStory) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress sx={{ color: "#ff6600" }} />
      </Box>
    );
  }

  if (!story) {
    return <Navigate to="/news" replace />;
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {story.title}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
                mb: 2,
              }}
            >
              <Chip
                icon={<TrendingUpIcon />}
                label={`${story.score} points`}
                size="small"
                sx={{ bgcolor: "#ff6600", color: "white" }}
              />
              <Chip
                icon={<PersonIcon />}
                label={story.by}
                size="small"
                variant="outlined"
              />
              <Chip
                icon={<AccessTimeIcon />}
                label={getTimeAgo(story.time)}
                size="small"
                variant="outlined"
              />
              {story.descendants !== undefined && (
                <Chip
                  icon={<CommentIcon />}
                  label={`${story.descendants} comments`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              TabIndicatorProps={{ sx: { bgcolor: "#ff6600" } }}
            >
              <Tab
                label="Article"
                disabled={!story.url}
                sx={{
                  "&.Mui-selected": { color: "#ff6600" },
                  textTransform: "none",
                }}
              />
              <Tab
                label={`Comments (${story.descendants || 0})`}
                sx={{
                  "&.Mui-selected": { color: "#ff6600" },
                  textTransform: "none",
                }}
              />
            </Tabs>
          </Box>
        </Container>

        {(iframeLoading || loadingComments) && (
          <LinearProgress
            sx={{ "& .MuiLinearProgress-bar": { bgcolor: "#ff6600" } }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            px: { xs: 2, lg: 3 },
            pb: 4,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 900 }}>
            {activeTab === 0 && story.url && (
              <Container maxWidth="md" disableGutters>
                <Box
                  sx={{ height: "calc(100vh - 250px)", position: "relative" }}
                >
                  <iframe
                    ref={iframeRef}
                    src={story.url}
                    title={story.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    onLoad={() => {
                      setIframeLoading(false);
                      setIframeError(false);
                    }}
                    onError={() => {
                      setIframeLoading(false);
                      setIframeError(true);
                    }}
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      right: 16,
                    }}
                  >
                    <Chip
                      label="Open Original"
                      icon={<OpenInNewIcon />}
                      component="a"
                      href={story.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      sx={{
                        backgroundColor: "#ff6600",
                        color: "white",
                        fontWeight: 500,
                        "&:hover": { backgroundColor: "#e65c00" },
                      }}
                    />
                  </Box>
                </Box>
              </Container>
            )}

            {activeTab === 0 && !story.url && (
              <Container maxWidth="md">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "400px",
                    p: 4,
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No article URL available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This is a text post. Check the comments tab for the
                    discussion.
                  </Typography>
                </Box>
              </Container>
            )}

            {activeTab === 1 && (
              <Container maxWidth="md" sx={{ py: 4 }}>
                {comments.length === 0 && !loadingComments ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "200px",
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      No comments yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Be the first to discuss this on Hacker News!
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                      {story.descendants || 0} Comments
                    </Typography>
                    {comments.map((comment) => (
                      <CommentItem key={comment.id} comment={comment} />
                    ))}
                  </>
                )}
              </Container>
            )}
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default HNStoryDetail;
